import { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import { useLanguage } from "../contexts/LanguageContext";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
} from "./ui/dialog";

// OUTPUT_SIZE: 800px supports ~14× density at 56px (nav avatar) and stays crisp
// when the image is expanded in the leaderboard/profile preview dialog (~400px).
const OUTPUT_SIZE = 800;
const OUTPUT_QUALITY = 0.82;

async function getCroppedBlob(imageSrc, croppedAreaPixels) {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.addEventListener("load", () => {
			const canvas = document.createElement("canvas");
			canvas.width = OUTPUT_SIZE;
			canvas.height = OUTPUT_SIZE;
			const ctx = canvas.getContext("2d");

			ctx.drawImage(
				image,
				croppedAreaPixels.x,
				croppedAreaPixels.y,
				croppedAreaPixels.width,
				croppedAreaPixels.height,
				0,
				0,
				OUTPUT_SIZE,
				OUTPUT_SIZE,
			);

			canvas.toBlob(
				(blob) => {
					if (!blob) {
						reject(new Error("Canvas is empty"));
						return;
					}
					resolve(blob);
				},
				"image/jpeg",
				OUTPUT_QUALITY,
			);
		});
		image.addEventListener("error", reject);
		image.src = imageSrc;
	});
}

export function AvatarCropDialog({ imageSrc, onConfirm, onCancel }) {
	const { t } = useLanguage();

	const [crop, setCrop] = useState({ x: 0, y: 0 });
	const [zoom, setZoom] = useState(1);
	const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
	const [processing, setProcessing] = useState(false);

	const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
		setCroppedAreaPixels(croppedAreaPixels);
	}, []);

	const handleConfirm = async () => {
		if (!croppedAreaPixels) return;
		setProcessing(true);
		try {
			const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
			onConfirm(blob);
		} finally {
			setProcessing(false);
		}
	};

	return (
		<Dialog
			open
			onOpenChange={(open) => {
				if (!open) onCancel();
			}}
		>
			<DialogContent className="bg-card border-border p-0 rounded-sm max-w-sm w-full">
				<DialogHeader className="px-4 pt-4 pb-2">
					<DialogTitle className="font-heading uppercase tracking-tight">
						{t("cropPhoto")}
					</DialogTitle>
					<DialogDescription className="hidden" />
				</DialogHeader>

				{/* Crop area — fixed square */}
				<div className="relative w-full" style={{ height: "320px" }}>
					<Cropper
						image={imageSrc}
						crop={crop}
						zoom={zoom}
						aspect={1}
						cropShape="rect"
						showGrid={false}
						onCropChange={setCrop}
						onCropComplete={onCropComplete}
						onZoomChange={setZoom}
						style={{
							containerStyle: { background: "#0A0A0A" },
							cropAreaStyle: { border: "2px solid #CCFF00" },
						}}
					/>
				</div>

				{/* Zoom slider */}
				<div className="px-4 pt-3 pb-1 flex items-center gap-3">
					<span className="label-tactical text-muted-foreground w-10 shrink-0">
						{t("cropZoom")}
					</span>
					<input
						type="range"
						min={1}
						max={3}
						step={0.01}
						value={zoom}
						onChange={(e) => setZoom(Number(e.target.value))}
						className="flex-1 accent-[#CCFF00]"
					/>
					<span className="label-tactical text-muted-foreground w-8 text-right shrink-0">
						{zoom.toFixed(1)}×
					</span>
				</div>

				{/* Actions */}
				<div className="flex gap-2 px-4 pb-4 pt-2">
					<Button
						variant="outline"
						size="sm"
						className="flex-1 rounded-sm"
						onClick={onCancel}
						disabled={processing}
					>
						{t("cropCancel")}
					</Button>
					<Button
						size="sm"
						className="flex-1 bg-volt hover:bg-volt/90 text-black rounded-sm font-heading uppercase tracking-tight"
						onClick={handleConfirm}
						disabled={processing}
					>
						{processing ? "..." : t("cropConfirm")}
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
}
